import type { RingBuffer } from './ringBuffer'
import type { ConsoleEntry } from '../types'
import { scrubCredentials } from './scrub'

type ConsoleLevel = 'log' | 'info' | 'warn' | 'error' | 'debug'

function safeStringify(arg: unknown): string {
  if (arg == null) return String(arg)
  if (typeof arg === 'string') return arg
  if (typeof arg === 'number' || typeof arg === 'boolean') return String(arg)
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`
  if (arg instanceof Element) return `<${arg.tagName.toLowerCase()}>`
  try {
    return JSON.stringify(arg, (_k, v) => (typeof v === 'bigint' ? v.toString() : v))
  } catch {
    try { return String(arg) } catch { return '[unserializable]' }
  }
}

export function installConsolePatch(buffer: RingBuffer<ConsoleEntry>): () => void {
  const levels: ConsoleLevel[] = ['log', 'info', 'warn', 'error', 'debug']
  const originals: Partial<Record<ConsoleLevel, (...args: unknown[]) => void>> = {}
  for (const level of levels) {
    const original = console[level] as ((...args: unknown[]) => void) | undefined
    if (typeof original !== 'function') continue
    originals[level] = original
    console[level] = function patched(...args: unknown[]) {
      try {
        const rawMessage = args.map(safeStringify).join(' ')
        const message = scrubCredentials(rawMessage).slice(0, 2000)
        const entry: ConsoleEntry = { level, message, ts: Date.now() }
        if (level === 'error') {
          const stack = new Error().stack
          if (stack) entry.stack = stack.split('\n').slice(2, 8).join('\n')
        }
        buffer.push(entry)
      } catch { /* never break console */ }
      original.apply(console, args)
    }
  }
  return () => {
    for (const [level, fn] of Object.entries(originals)) {
      (console as unknown as Record<string, (...args: unknown[]) => void>)[level] = fn
    }
  }
}
